import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Max height as percentage of viewport, default 85 */
  maxHeight?: number;
  /** Render as centered modal on desktop */
  desktopMode?: 'hidden' | 'modal';
  /** Desktop modal max width class, default max-w-2xl */
  desktopMaxWidthClass?: string;
  /** Extra classes for mobile + desktop panel */
  panelClassName?: string;
  /** Extra classes for desktop panel only */
  desktopPanelClassName?: string;
  /** Extra classes for scrollable content container */
  contentClassName?: string;
  /** Show drag handle on mobile, default true */
  showHandle?: boolean;
  /** Allow closing by backdrop click, default true */
  closeOnBackdrop?: boolean;
  /** Override z-index class, default z-[9999] */
  zIndexClassName?: string;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  maxHeight = 85,
  desktopMode = 'hidden',
  desktopMaxWidthClass = 'max-w-2xl',
  panelClassName = '',
  desktopPanelClassName = '',
  contentClassName = '',
  showHandle = true,
  closeOnBackdrop = true,
  zIndexClassName = 'z-[9999]'
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isDesktopModal = desktopMode === 'modal';

  return createPortal(
    <div className={`fixed inset-0 ${zIndexClassName}`}>
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in ${isDesktopModal ? '' : 'lg:hidden'}`}
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      {/* Mobile sheet */}
      <div className="absolute inset-0 flex items-end lg:hidden pointer-events-none">
        <div
          ref={sheetRef}
          className={`pointer-events-auto w-full bg-white rounded-t-2xl shadow-2xl flex flex-col animate-sheet-up ${panelClassName}`}
          style={{ maxHeight: `${maxHeight}vh` }}
        >
          {showHandle && (
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
          )}

          {title && (
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-semibold text-content">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 text-content/40 hover:text-content hover:bg-gray-100 rounded-full transition-colors active:scale-90"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className={`flex-1 overflow-y-auto overscroll-contain ${contentClassName}`}>
            {children}
          </div>
        </div>
      </div>

      {/* Desktop centered modal */}
      {isDesktopModal && (
        <div className="hidden lg:flex absolute inset-0 items-center justify-center p-6 pointer-events-none">
          <div className={`pointer-events-auto w-full bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-fade-in ${desktopMaxWidthClass} ${panelClassName} ${desktopPanelClassName}`}>
            {title && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                <h2 className="text-xl font-semibold text-content">{title}</h2>
                <button
                  onClick={onClose}
                  className="p-2 -mr-2 text-content/40 hover:text-content hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}

            <div className={`flex-1 overflow-y-auto overscroll-contain ${contentClassName}`}>
              {children}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
